import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Dialog } from '@/components/ui/Dialog';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Button } from '@/components/ui/Button';
import { api } from '@/lib/api';
import { useFarmStore } from '@/stores/farmStore';

interface FarmCreateModalProps {
  open: boolean;
  onClose: () => void;
}

interface FarmFormData {
  name: string;
  location: string;
  timezone: string;
}

const TIMEZONE_OPTIONS = [
  { value: 'Asia/Manila', label: 'Philippines (Asia/Manila)' },
  { value: 'Asia/Singapore', label: 'Singapore (Asia/Singapore)' },
  { value: 'Asia/Bangkok', label: 'Thailand (Asia/Bangkok)' },
  { value: 'Asia/Ho_Chi_Minh', label: 'Vietnam (Asia/Ho_Chi_Minh)' },
  { value: 'Asia/Jakarta', label: 'Indonesia (Asia/Jakarta)' },
];

interface CreateFarmResponse {
  data: {
    id: string;
    name: string;
  };
}

export function FarmCreateModal({ open, onClose }: FarmCreateModalProps) {
  const queryClient = useQueryClient();
  const { setSelectedFarmId } = useFarmStore();
  const [formData, setFormData] = useState<FarmFormData>({
    name: '',
    location: '',
    timezone: 'Asia/Manila',
  });
  const [errors, setErrors] = useState<Partial<Record<keyof FarmFormData, string>>>({});

  const createMutation = useMutation({
    mutationFn: async (data: FarmFormData) => {
      const payload: Record<string, unknown> = {
        name: data.name.trim(),
        timezone: data.timezone,
      };

      if (data.location.trim()) {
        payload.location = data.location.trim();
      }

      return api.post<CreateFarmResponse>('/api/v1/farms', payload);
    },
    onSuccess: async (response) => {
      toast.success(`Farm "${response.data.name}" created successfully`);
      // The farm list must contain the new farm before it is selected, or the
      // selection reconcile resets to the first farm; then refetch the rest scoped to it.
      await queryClient.invalidateQueries({ queryKey: ['farms'] });
      setSelectedFarmId(response.data.id);
      await queryClient.invalidateQueries();
      handleClose();
    },
    onError: (error: Error) => {
      if (error.message.includes('already have a farm')) {
        setErrors({ name: 'You already have a farm with this name' });
      } else {
        toast.error(error.message || 'Failed to create farm');
      }
    },
  });

  const handleClose = () => {
    setFormData({
      name: '',
      location: '',
      timezone: 'Asia/Manila',
    });
    setErrors({});
    onClose();
  };

  const handleChange = (field: keyof FarmFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof FarmFormData, string>> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Farm name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      createMutation.mutate(formData);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} title="Create New Farm" size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Farm Name"
          placeholder="e.g., ABC Aquaculture Farm"
          value={formData.name}
          onChange={(e) => handleChange('name', e.target.value)}
          error={errors.name}
          autoFocus
        />

        <Input
          label="Location"
          placeholder="e.g., Pangasinan, Philippines"
          value={formData.location}
          onChange={(e) => handleChange('location', e.target.value)}
          description="City, province, or region"
        />

        <Select
          label="Timezone"
          value={formData.timezone}
          onChange={(e) => handleChange('timezone', e.target.value)}
          options={TIMEZONE_OPTIONS}
        />

        <div className="flex justify-end gap-3 pt-4">
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            disabled={createMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={createMutation.isPending}
          >
            Create Farm
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
